import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Badge, Box, Button, Heading, Spinner, Table } from '@hyphen/hyphen-components';

import { api } from '../lib/api.js';
import { useResource } from '../lib/session.js';
import { RunStatus, When } from './evidence-shared.jsx';

/**
 * Evidence collection: which collectors can run here, a way to run them, and
 * every run so far.
 */
export function EvidenceAdmin({ canEdit }) {
  const { data: sources, error: sourcesError } = useResource('/evidence/sources');
  const { data: runs, error: runsError, reload: reloadRuns } = useResource('/evidence/runs');
  const [collecting, setCollecting] = useState(false);
  const [error, setError] = useState(null);

  async function collect() {
    setCollecting(true);
    setError(null);
    try {
      await api.post('/evidence/runs');
      await reloadRuns();
    } catch (err) {
      setError(err.message);
    } finally {
      setCollecting(false);
    }
  }

  const ready = (sources ?? []).filter((source) => source.ready).length;

  return (
    <Box gap="4xl">
      <Box direction="row" alignItems="center" gap="xl">
        <Heading as="h1" size="lg">Evidence</Heading>
        {canEdit && (
          <Box direction="row" alignItems="center" gap="lg" style={{ marginLeft: 'auto' }}>
            {collecting && (
              <Box as="span" fontSize="sm" color="secondary">
                Collecting — this can take a minute
              </Box>
            )}
            <Button
              size="sm"
              isLoading={collecting}
              isDisabled={!sources || ready === 0}
              title={sources && ready === 0 ? 'No collector is configured on this server' : undefined}
              onClick={collect}
            >
              Collect now
            </Button>
          </Box>
        )}
      </Box>

      {error && <Alert variant="danger">{error}</Alert>}

      <Box gap="xl">
        <Heading as="h2" size="md">Collectors</Heading>
        {sourcesError && <Alert variant="warning">{sourcesError}</Alert>}
        {!sources && !sourcesError && <Spinner />}
        {sources && <Sources sources={sources} />}
      </Box>

      <Box gap="xl">
        <Heading as="h2" size="md">Runs</Heading>
        {runsError && <Alert variant="warning">{runsError}</Alert>}
        {!runs && !runsError && <Spinner />}
        {runs && runs.length === 0 && (
          <Box color="secondary">Nothing has been collected yet.</Box>
        )}
        {runs && runs.length > 0 && <Runs runs={runs} />}
      </Box>
    </Box>
  );
}

function Sources({ sources }) {
  const columns = [
    { heading: 'Collector', dataKey: 'name', width: 140 },
    {
      heading: 'Status',
      dataKey: 'ready',
      width: 120,
      render: (_cell, row) => (
        <Badge variant="soft" color={row.ready ? 'green' : 'grey'}>
          {row.ready ? 'ready' : 'not configured'}
        </Badge>
      ),
    },
    {
      // Ready: who it would collect as, because "who asked" is part of what
      // makes the answer trustworthy. Not ready: exactly what is missing.
      heading: 'Details',
      dataKey: 'identity',
      render: (_cell, row) => row.ready ? (
        <Box as="span" fontSize="sm">{row.identity}</Box>
      ) : (
        <Box gap="xs" fontSize="sm" color="secondary">
          {row.missing.map((line) => <span key={line}>{line}</span>)}
        </Box>
      ),
    },
    {
      heading: 'Controls',
      dataKey: 'controls',
      render: (_cell, row) => (
        <Box as="span" fontSize="sm" color="secondary">{row.controls.join(', ')}</Box>
      ),
    },
  ];
  return <Table rowKey="name" columns={columns} rows={sources} isScrollable={{ x: true }} />;
}

function Runs({ runs }) {
  const columns = [
    {
      heading: 'Started',
      dataKey: 'started_at',
      width: 190,
      render: (_cell, row) => <Link to={`/admin/evidence/runs/${row.id}`}><When iso={row.started_at} /></Link>,
    },
    { heading: 'Collector', dataKey: 'source', width: 120 },
    { heading: 'Result', dataKey: 'status', width: 120, render: (_cell, row) => <RunStatus status={row.status} /> },
    { heading: 'Artifacts', dataKey: 'artifacts', width: 90 },
    {
      heading: 'Issues',
      dataKey: 'issues',
      width: 80,
      render: (_cell, row) => row.issues > 0
        ? <Badge variant="soft" color="red">{row.issues}</Badge>
        : <span>0</span>,
    },
    { heading: 'Notes', dataKey: 'notes', width: 80 },
    {
      heading: 'By',
      dataKey: 'triggered_by',
      render: (_cell, row) => <Box as="span" fontSize="sm" color="secondary">{row.triggered_by}</Box>,
    },
  ];
  return <Table rowKey="id" columns={columns} rows={runs} isScrollable={{ x: true }} />;
}
